import React from "react";
import "./FaceScanMesh.css";

/**
 * The biometric face-mesh motif for the Live Sync console: a triangulated
 * wireframe over a face silhouette, glowing vertex nodes, a beam that sweeps
 * down the frame, and detection brackets at the corners.
 *
 * It is inline SVG rather than an image so it stays crisp at any size, themes
 * with CSS, and costs nothing to ship. All motion is CSS — this page can sit
 * open all day beside a shoot, so there is no animation loop burning battery,
 * and the whole thing stills under prefers-reduced-motion.
 *
 * `active` drives the animation: an idle console should look idle.
 */

// Landmarks on a 200×240 canvas, mirrored around x=100. Decorative, not
// anatomical — the shape reads as a face at a glance, which is the whole job.
const P = [
	[100, 14], [64, 24], [136, 24], [36, 52], [164, 52], [26, 92], [174, 92],
	[60, 74], [86, 68], [114, 68], [140, 74],
	[58, 98], [73, 92], [88, 98], [73, 105],
	[112, 98], [127, 92], [142, 98], [127, 105],
	[100, 86], [100, 112], [100, 134], [86, 140], [114, 140],
	[34, 124], [166, 124], [50, 152], [150, 152],
	[78, 162], [100, 157], [122, 162], [100, 174],
	[42, 178], [158, 178], [70, 202], [130, 202], [100, 216],
];

const TRIANGLES = [
	// forehead
	[0, 1, 8], [0, 8, 9], [0, 9, 2], [1, 3, 7], [1, 7, 8], [2, 10, 4], [2, 9, 10],
	[3, 5, 7], [4, 10, 6],
	// eyes and brows
	[5, 7, 11], [7, 8, 12], [7, 12, 11], [8, 13, 12], [8, 19, 13],
	[6, 10, 17], [10, 9, 16], [10, 16, 17], [9, 15, 16], [9, 19, 15],
	// nose
	[19, 13, 15], [13, 20, 15], [13, 14, 20], [15, 18, 20],
	[20, 22, 21], [20, 21, 23], [22, 23, 21],
	// cheeks
	[5, 11, 24], [11, 14, 24], [14, 20, 22], [24, 14, 26], [22, 26, 14],
	[6, 17, 25], [17, 18, 25], [18, 20, 23], [25, 18, 27], [23, 27, 18],
	// mouth and jaw
	[22, 28, 26], [22, 29, 28], [22, 23, 29], [23, 30, 29], [23, 27, 30],
	[28, 29, 31], [29, 30, 31], [24, 26, 32], [25, 27, 33],
	[26, 28, 34], [27, 30, 35], [28, 31, 34], [30, 35, 31],
	[32, 34, 26], [33, 35, 27], [34, 31, 36], [31, 35, 36],
];

export default function FaceScanMesh({ active = false, size = 132 }) {
	return (
		<div
			className="fsm"
			data-active={active}
			style={{ width: size, height: size * 1.16 }}
			aria-hidden="true"
		>
			<svg viewBox="0 0 200 240" className="fsm-svg">
				<defs>
					<linearGradient id="fsm-beam" x1="0" x2="1" y1="0" y2="0">
						<stop offset="0%" stopColor="transparent" />
						<stop offset="50%" stopColor="#67e8f9" />
						<stop offset="100%" stopColor="transparent" />
					</linearGradient>
					<radialGradient id="fsm-face" cx="50%" cy="42%" r="62%">
						<stop offset="0%" stopColor="rgba(56,189,248,0.20)" />
						<stop offset="100%" stopColor="rgba(56,189,248,0)" />
					</radialGradient>
				</defs>

				{/* Head silhouette behind the mesh, so the wireframe reads as being
				    laid over something rather than floating. */}
				<ellipse cx="100" cy="118" rx="76" ry="102" fill="url(#fsm-face)" />

				<g className="fsm-mesh">
					{TRIANGLES.map(([a, b, c], i) => (
						<polygon
							key={i}
							points={`${P[a][0]},${P[a][1]} ${P[b][0]},${P[b][1]} ${P[c][0]},${P[c][1]}`}
							className="fsm-tri"
							style={{ animationDelay: `${(i % 12) * 0.16}s` }}
						/>
					))}
				</g>

				<g className="fsm-nodes">
					{P.map(([x, y], i) => (
						<circle
							key={i}
							cx={x}
							cy={y}
							r="2.1"
							className="fsm-node"
							style={{ animationDelay: `${(i % 9) * 0.22}s` }}
						/>
					))}
				</g>

				{/* Alignment crosshair, as on a biometric capture frame. */}
				<line x1="100" y1="0" x2="100" y2="240" className="fsm-cross" />
				<line x1="0" y1="98" x2="200" y2="98" className="fsm-cross" />

				{/* Eyes: the one place the reference puts real colour. Drawn over
				    open sockets, so they read as eyes rather than vertices. */}
				<circle cx="73" cy="98" r="6.2" className="fsm-iris" />
				<circle cx="127" cy="98" r="6.2" className="fsm-iris" />
				<circle cx="73" cy="98" r="2.6" className="fsm-eye" />
				<circle cx="127" cy="98" r="2.6" className="fsm-eye" />

				<rect className="fsm-beam" x="4" y="0" width="192" height="2.4" fill="url(#fsm-beam)" />
			</svg>

			<span className="fsm-bracket fsm-bracket--tl" />
			<span className="fsm-bracket fsm-bracket--tr" />
			<span className="fsm-bracket fsm-bracket--bl" />
			<span className="fsm-bracket fsm-bracket--br" />
		</div>
	);
}
